import React, { useCallback, useEffect, useMemo, useState } from "react";
import EpsfPageBreadcrumb from "./EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { getUserData } from "../../../services/apiService";
import {
  getSessionRoleName,
  isSessionSuperAdmin,
  isSystemAdministratorRole,
} from "../../../utils/permissionUtils";
import { EpsfAccessGate, useEpsfAccess } from "./epsfAccess";
import { useEpsfStudySite } from "./epsfLedgerUi";
import {
  formatFormTriggerLabel,
  renderQueueStatusBadge,
} from "./epsfBadges";
import EpsfFormFillModal from "./EpsfFormFillModal";
import {
  ensureRecurringFormDues,
  fetchFormOccurrences,
  fetchFormWorkItems,
  linkFormInstance,
} from "./epsfService";
import { createInstance } from "../esource/esourceService";
import "./EpsfPages.scss";

const QUEUE_FIELD_OPTIONS = [
  { key: "Form", label: "Form" },
  { key: "Source", label: "Source" },
  { key: "Due", label: "Due" },
  { key: "Status", label: "Status" },
];

const QUEUE_DEFAULT_COLUMNS = ["Form", "Source", "Due", "Status"];

const GUID_RE =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;

function normalizeGuid(value) {
  return String(value || "")
    .replace(/[{}]/g, "")
    .trim()
    .toLowerCase();
}

function userCanFillFormWorkItem(userData, workItem) {
  if (!workItem) return false;
  const workType = String(
    workItem.WorkType || workItem.workType || "COMPLETE",
  ).toUpperCase();
  if (workType === "REVIEW") return false;

  const sessionRole = getSessionRoleName(userData);
  if (
    isSystemAdministratorRole(sessionRole) ||
    isSessionSuperAdmin(userData)
  ) {
    return true;
  }

  const requiredRoleId = normalizeGuid(
    workItem.ResponsibleRoleId ||
      workItem.responsibleRoleId ||
      workItem.CompletedByRoleId ||
      workItem.completedByRoleId,
  );
  const userRoleId = normalizeGuid(
    userData?.RoleId || userData?.roleId || userData?.RolesId,
  );
  if (requiredRoleId && userRoleId && requiredRoleId === userRoleId) {
    return true;
  }

  const requiredRole = String(
    workItem.ResponsibleRole ||
      workItem.responsibleRole ||
      workItem.AssigneeRole ||
      workItem.assigneeRole ||
      "",
  )
    .trim()
    .toLowerCase();
  if (!requiredRole) return true;
  return sessionRole.trim().toLowerCase() === requiredRole;
}

function userCanReviewFormWorkItem(userData, workItem) {
  if (!workItem) return false;
  const workType = String(
    workItem.WorkType || workItem.workType || "",
  ).toUpperCase();
  if (workType !== "REVIEW") return false;

  const sessionRole = getSessionRoleName(userData);
  if (
    isSystemAdministratorRole(sessionRole) ||
    isSessionSuperAdmin(userData)
  ) {
    return true;
  }

  const requiredRoleId = normalizeGuid(
    workItem.ResponsibleRoleId || workItem.responsibleRoleId,
  );
  const userRoleId = normalizeGuid(
    userData?.RoleId || userData?.roleId || userData?.RolesId,
  );
  if (requiredRoleId && userRoleId && requiredRoleId === userRoleId) {
    return true;
  }

  const requiredRole = String(
    workItem.ResponsibleRole || workItem.responsibleRole || "",
  )
    .trim()
    .toLowerCase();
  if (!requiredRole) return false;
  return sessionRole.trim().toLowerCase() === requiredRole;
}

function formatWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortRef(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (GUID_RE.test(raw)) {
    GUID_RE.lastIndex = 0;
    return raw.slice(0, 8).toUpperCase();
  }
  GUID_RE.lastIndex = 0;
  return raw.length > 18 ? `${raw.slice(0, 16)}…` : raw;
}

function cleanTitleText(value) {
  return String(value || "")
    .replace(GUID_RE, " ")
    .replace(/\{[^}]+\}/g, " ")
    .replace(/[|/\\]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function resolveFormTitle(row) {
  const template = String(
    row.TemplateName ||
      row.templateName ||
      row.TemplateCode ||
      row.templateCode ||
      "",
  ).trim();
  const cleaned = cleanTitleText(row.Title || row.title);
  const trigger = formatFormTriggerLabel(
    row.TriggerCode || row.triggerCode || "",
  );
  const looksLikeContextOnly =
    !!cleaned &&
    cleaned.split(/\s+/).length <= 3 &&
    !/form|receipt|dispense|destroy|reconcil|temp|check|manual/i.test(cleaned);

  if (template && (!cleaned || looksLikeContextOnly || cleaned === trigger)) {
    return template;
  }
  if (cleaned) return cleaned;
  if (template) return template;
  if (trigger) return trigger;
  return "Pharmacy form";
}

function workItemOpen(row) {
  const status = String(
    row.WorkItemStatus || row.workItemStatus || row.Status || row.status || "",
  ).toUpperCase();
  return status === "OPEN" || status === "IN_PROGRESS" || !status;
}

function workItemPriority(row) {
  const type = String(row.WorkType || row.workType || "COMPLETE").toUpperCase();
  const open = workItemOpen(row) ? 100 : 0;
  if (type === "REVIEW") return open + 40;
  if (type === "CORRECT") return open + 30;
  if (type === "COMPLETE") return open + 20;
  return open + 10;
}

function isDuePast(row) {
  const dueRaw = row.DueAtUtc || row.dueAtUtc || row.DueAt || row.dueAt;
  if (!dueRaw) return false;
  const dueMs = new Date(dueRaw).getTime();
  return !Number.isNaN(dueMs) && dueMs < Date.now();
}

/**
 * Form work lifecycle (fill → approval → correction → approved).
 * Overdue is phase-specific so the queue stays clear for staff.
 */
function deriveQueueStatus(row) {
  const workType = String(row.WorkType || row.workType || "").toUpperCase();
  const workflow = String(
    row.WorkflowStatus || row.workflowStatus || "",
  ).toUpperCase();
  const occStatus = String(
    row.OccurrenceStatus || row.occurrenceStatus || "",
  ).toUpperCase();
  const overdue = isDuePast(row);

  if (
    workflow === "APPROVED" ||
    workflow === "COMPLETED" ||
    occStatus === "COMPLETE"
  ) {
    return "Approved";
  }

  const inApproval =
    (workType === "REVIEW" && workItemOpen(row)) ||
    workflow === "AWAITING_REVIEW" ||
    workflow === "SUBMITTED";
  if (inApproval) {
    return overdue ? "Approval overdue" : "Waiting for approval";
  }

  const inCorrection =
    (workType === "CORRECT" && workItemOpen(row)) ||
    workflow === "CORRECTION_REQUIRED" ||
    workflow === "REJECTED";
  if (inCorrection) {
    return overdue ? "Correction overdue" : "Waiting for correction";
  }

  return overdue ? "Fill overdue" : "Waiting for fill";
}

function isFillPhaseStatus(status) {
  return (
    status === "Waiting for fill" ||
    status === "Fill overdue" ||
    status === "Waiting for correction" ||
    status === "Correction overdue"
  );
}

function isCorrectionPhaseStatus(status) {
  return (
    status === "Waiting for correction" || status === "Correction overdue"
  );
}

function isApprovalPhaseStatus(status) {
  return status === "Waiting for approval" || status === "Approval overdue";
}

function isApprovedStatus(status) {
  return status === "Approved";
}

/** One primary action icon for the current status (no ⋮ menu). */
function resolveStatusAction(row, user) {
  const status = deriveQueueStatus(row);
  const hasInstance = !!(row.FormInstanceId || row.formInstanceId);

  if (isFillPhaseStatus(status)) {
    const canFill = userCanFillFormWorkItem(user, {
      ...row,
      WorkType: isCorrectionPhaseStatus(status)
        ? "CORRECT"
        : row.WorkType || "COMPLETE",
    });
    if (!canFill) return null;
    return {
      mode: "fill",
      icon: "fas fa-pen",
      label: isCorrectionPhaseStatus(status) ? "Correct form" : "Fill form",
    };
  }

  if (isApprovalPhaseStatus(status)) {
    if (
      userCanReviewFormWorkItem(user, {
        ...row,
        WorkType: "REVIEW",
      })
    ) {
      return {
        mode: "review",
        icon: "fas fa-clipboard-check",
        label: "Review / approve",
      };
    }
    if (hasInstance) {
      return {
        mode: "pending",
        icon: "fas fa-eye",
        label: "View form",
      };
    }
    return null;
  }

  if (isApprovedStatus(status) && hasInstance) {
    return {
      mode: "view",
      icon: "fas fa-eye",
      label: "View form",
    };
  }

  return null;
}

function mapOccurrenceToQueueRow(row) {
  return {
    ...row,
    WorkItemId: null,
    WorkType: "COMPLETE",
    Title: row.Title || row.title,
    TriggerCode: row.TriggerCode || row.triggerCode,
    LinkedTxnId: row.LinkedTxnId || row.linkedTxnId,
    DueAtUtc: row.DueAtUtc || row.dueAtUtc || row.DueAt || row.dueAt,
    WorkflowStatus: row.WorkflowStatus || row.workflowStatus,
    FormOccurrenceId: row.FormOccurrenceId || row.formOccurrenceId,
    FormInstanceId: row.FormInstanceId || row.formInstanceId,
    FormTemplateId: row.FormTemplateId || row.formTemplateId,
    TemplateName: row.TemplateName || row.templateName,
    TemplateCode: row.TemplateCode || row.templateCode,
    OccurrenceStatus: row.Status || row.status,
    WorkItemStatus: "OPEN",
    _fromOccurrence: true,
  };
}

/** One row per occurrence — prefer open REVIEW / CORRECT / COMPLETE work item. */
function mergeQueueRows(workRows, occRows) {
  const byOcc = new Map();

  (workRows || []).forEach((row) => {
    const occId = String(row.FormOccurrenceId || row.formOccurrenceId || "");
    if (!occId) return;
    const prev = byOcc.get(occId);
    if (!prev || workItemPriority(row) > workItemPriority(prev)) {
      byOcc.set(occId, row);
    }
  });

  (occRows || []).forEach((raw) => {
    const row = mapOccurrenceToQueueRow(raw);
    const occId = String(row.FormOccurrenceId || "");
    if (!occId || byOcc.has(occId)) return;
    byOcc.set(occId, row);
  });

  return Array.from(byOcc.values()).sort((a, b) => {
    const da = new Date(a.DueAtUtc || a.dueAtUtc || 0).getTime() || 0;
    const db = new Date(b.DueAtUtc || b.dueAtUtc || 0).getTime() || 0;
    return da - db;
  });
}

function resolveModalMode(row, user) {
  const status = deriveQueueStatus(row);
  if (isApprovedStatus(status)) return "view";
  if (isApprovalPhaseStatus(status)) {
    return userCanReviewFormWorkItem(user, {
      ...row,
      WorkType: "REVIEW",
    })
      ? "review"
      : "pending";
  }
  return "fill";
}

export default function EpsfDueForms() {
  const { loading, canView, moduleAccess } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const user = useMemo(() => getUserData() || {}, []);

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [formFillModal, setFormFillModal] = useState(null);

  const refresh = useCallback(async () => {
    setError("");
    setListLoading(true);
    try {
      if (!ctx.studyId || !ctx.siteId) {
        setRows([]);
        return;
      }
      try {
        await ensureRecurringFormDues(ctx.studyId, ctx.siteId);
      } catch (ensureErr) {
        console.warn("ensureRecurringFormDues failed", ensureErr);
      }

      const [work, occ] = await Promise.all([
        fetchFormWorkItems(ctx.studyId, ctx.siteId, "*"),
        fetchFormOccurrences(ctx.studyId, ctx.siteId),
      ]);

      if (!work.success && !occ.success) {
        setError(work.message || occ.message || "Could not load due forms.");
        setRows([]);
        return;
      }

      setRows(mergeQueueRows(work.rows || [], occ.rows || []));
    } catch (err) {
      setRows([]);
      setError(
        err?.message === "Request timeout"
          ? "Timed out loading due forms. Refresh and try again."
          : err?.message || "Could not load due forms.",
      );
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openFormSheet = useCallback(
    async (row, forcedMode = null) => {
      setError("");
      const mode = forcedMode || resolveModalMode(row, user);
      const status = deriveQueueStatus(row);

      if (mode === "fill" || mode === "correct") {
        if (!userCanFillFormWorkItem(user, row)) {
          showAlert({
            title: "Not authorised",
            message: "You are not authorised to fill this form.",
            type: "info",
          });
          return;
        }
      }

      setBusy(true);
      try {
        let instanceId = row.FormInstanceId || row.formInstanceId || null;
        const occurrenceId = row.FormOccurrenceId || row.formOccurrenceId;
        const templateId = row.FormTemplateId || row.formTemplateId;

        if (!instanceId) {
          if (mode === "review" || mode === "pending" || mode === "view") {
            showAlert({
              title: "Form unavailable",
              message: "No submitted form instance is linked yet.",
              type: "info",
            });
            return;
          }
          if (!templateId) {
            showAlert({
              title: "Form unavailable",
              message: "No form template is mapped to this requirement.",
              type: "info",
            });
            return;
          }
          const created = await createInstance({
            FormTemplateId: templateId,
            StudyId: ctx.studyId,
            SiteId: ctx.siteId,
            Title: resolveFormTitle(row),
          });
          if (!created.success || !created.formInstanceId) {
            showAlert({
              title: "Could not open form",
              message:
                created.message ||
                "Could not create form instance. Is e-Source licensed?",
              type: "error",
            });
            return;
          }
          instanceId = created.formInstanceId;
          const linked = await linkFormInstance({
            FormOccurrenceId: occurrenceId,
            FormInstanceId: instanceId,
          });
          if (!linked.success) {
            showAlert({
              title: "Link failed",
              message: linked.message || "Instance created but link failed.",
              type: "error",
            });
            return;
          }
        }

        setFormFillModal({
          formInstanceId: instanceId,
          workItem: {
            ...row,
            FormInstanceId: instanceId,
            LinkedTxnId: row.LinkedTxnId || row.linkedTxnId || null,
            FormOccurrenceId: occurrenceId,
            StudyId: row.StudyId || row.studyId || ctx.studyId,
            SiteId: row.SiteId || row.siteId || ctx.siteId,
          },
          mode: isCorrectionPhaseStatus(status) && mode === "fill" ? "fill" : mode,
        });
      } finally {
        setBusy(false);
      }
    },
    [ctx.siteId, ctx.studyId, showAlert, user],
  );

  const renderCell = useCallback((row, field) => {
    if (field === "Form") {
      const title = resolveFormTitle(row);
      const template = String(
        row.TemplateName || row.templateName || "",
      ).trim();
      const showMeta = template && template !== title;
      return (
        <div className="epsf-due-form-cell" title={title}>
          <span className="epsf-due-form-cell__title">{title}</span>
          {showMeta ? (
            <span className="epsf-due-form-cell__meta">{template}</span>
          ) : null}
        </div>
      );
    }
    if (field === "Source") {
      const triggerRaw = row.TriggerCode || row.triggerCode || "";
      const trigger = formatFormTriggerLabel(triggerRaw);
      const linked =
        row.LinkedTxnId ||
        row.linkedTxnId ||
        row.ReferenceNo ||
        row.referenceNo ||
        "";
      const ref = shortRef(linked);
      if (!trigger && !ref) return "—";
      return (
        <div
          className="epsf-due-source-cell"
          title={linked ? String(linked) : trigger || undefined}
        >
          <span className="epsf-due-source-cell__trigger">
            {trigger || "Event"}
          </span>
          {ref ? (
            <span className="epsf-due-source-cell__ref">{ref}</span>
          ) : null}
        </div>
      );
    }
    if (field === "Due") {
      return formatWhen(
        row.DueAtUtc || row.dueAtUtc || row.DueAt || row.dueAt,
      );
    }
    if (field === "Status") {
      const status = deriveQueueStatus(row);
      const action = resolveStatusAction(row, user);
      return (
        <div className="epsf-due-status-cell">
          {renderQueueStatusBadge(status)}
          {action ? (
            <button
              type="button"
              className="epsf-due-status-action"
              title={action.label}
              aria-label={action.label}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                openFormSheet(row, action.mode);
              }}
            >
              <i className={action.icon} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      );
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, [busy, openFormSheet, user]);

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "Due forms" },
          ]}
        />
        <div className="epsf-receive">
          {error ? <p className="epsf-receive__error">{error}</p> : null}

          <div className="epsf-report-wrap epsf-due-queue">
            <ReportTable
              rows={rows}
              loading={listLoading}
              fieldOptions={QUEUE_FIELD_OPTIONS}
              defaultColumns={QUEUE_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                QUEUE_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
              }
              renderCell={renderCell}
              getRowKey={(row, index) =>
                String(
                  row.FormOccurrenceId ||
                    row.formOccurrenceId ||
                    row.WorkItemId ||
                    row.workItemId ||
                    index,
                )
              }
              persistLayoutKey="epsf-due-work-queue-v4"
              emptyMessage={
                ctx.studyId && ctx.siteId
                  ? "No form work items for this study and site."
                  : "Select study and site to load the form queue."
              }
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <EpsfFormFillModal
        open={Boolean(formFillModal?.formInstanceId)}
        formInstanceId={formFillModal?.formInstanceId || ""}
        workItem={formFillModal?.workItem || null}
        mode={formFillModal?.mode || "fill"}
        onClose={() => setFormFillModal(null)}
        onSubmitted={async () => {
          setFormFillModal(null);
          await refresh();
          showAlert({
            title: "Form submitted",
            message:
              "Form submitted. If approval is required, status shows as Waiting for approval for the approver role.",
            type: "success",
          });
        }}
        onApproved={async (result) => {
          setFormFillModal(null);
          await refresh();
          const wf = String(
            result?.rows?.[0]?.WorkflowStatus ||
              result?.WorkflowStatus ||
              "",
          ).toUpperCase();
          showAlert({
            title:
              wf === "CORRECTION_REQUIRED" ? "Form rejected" : "Form approved",
            message:
              wf === "CORRECTION_REQUIRED"
                ? "Form sent back for correction. Completing staff can fill and resubmit from this queue."
                : "Form approved and locked. Answers can no longer be changed.",
            type: wf === "CORRECTION_REQUIRED" ? "info" : "success",
          });
        }}
      />
      <InfoAlert {...alertState} onClose={hideAlert} />
    </EpsfAccessGate>
  );
}
